import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Library, Plus, Folder, FileText, Search, Loader2, Check, X, Clock, Sparkles } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import {
  useKnowledgeBaseTree,
  useKnowledgeBaseItem,
  useCreateKnowledgeBaseItem,
  useSaveKnowledgeBaseItem,
  useDeleteKnowledgeBaseItem,
  useMoveKnowledgeBaseItem,
  useApproveKnowledgeBaseItem,
  useRejectKnowledgeBaseItem,
  useMarkPendingKnowledgeBaseItem,
} from '@/hooks/useKnowledgeBase';
import type { KnowledgeBaseItem } from '@/services/knowledgeBase.service';
import FolderTree from '@/components/knowledge-base/FolderTree';
import ArticleEditor from '@/components/knowledge-base/ArticleEditor';
import MoveItemDialog from '@/components/knowledge-base/MoveItemDialog';
import { ConsolidateKBDialog } from '@/components/knowledge-base/ConsolidateKBDialog';

type CreateMode = { type: 'folder' | 'article'; parentId: string | null } | null;

export default function KnowledgeBase() {
  const { id: urlArticleId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialEditing = searchParams.get('edit') === 'true';
  const { user } = useAppAuth();
  const canManageKB = useMemo(() => {
    if (!user) return false;
    const roles = (user.roles || []).map((r: any) =>
      (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase()
    );
    return !(roles.length === 1 && roles[0] === 'readonly_partner');
  }, [user]);
  const [selectedId, _setSelectedId] = useState<string | null>(urlArticleId || null);
  const setSelectedId = useCallback((id: string | null, itemType?: string) => {
    _setSelectedId(id);
    if (id && itemType === 'article') {
      navigate(`/knowledge-base/article/${id}`, { replace: true });
    } else if (!id || itemType === 'folder') {
      navigate('/knowledge-base', { replace: true });
    }
  }, [navigate]);
  const [searchQuery, setSearchQuery] = useState('');
  const [createMode, setCreateMode] = useState<CreateMode>(null);
  const [createName, setCreateName] = useState('');
  const [renameItem, setRenameItem] = useState<KnowledgeBaseItem | null>(null);
  const [renameName, setRenameName] = useState('');
  const [moveItem, setMoveItem] = useState<KnowledgeBaseItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<KnowledgeBaseItem | null>(null);
  const [consolidateOpen, setConsolidateOpen] = useState(false);

  const { data: treeData, isLoading } = useKnowledgeBaseTree();
  const items = treeData?.records || [];

  const { data: selectedArticle } = useKnowledgeBaseItem(
    selectedId && items.find(i => i.id === selectedId)?.item_type === 'article' ? selectedId : null
  );

  const pendingUpdateId = selectedId
    ? items.find(i => i.original_item_id === selectedId && i.status === 'pending')?.id ?? null
    : null;
  const { data: pendingUpdateArticle } = useKnowledgeBaseItem(pendingUpdateId);

  const createMutation = useCreateKnowledgeBaseItem();
  const saveMutation = useSaveKnowledgeBaseItem();
  const deleteMutation = useDeleteKnowledgeBaseItem();
  const moveMutation = useMoveKnowledgeBaseItem();
  const approveMutation = useApproveKnowledgeBaseItem();
  const rejectMutation = useRejectKnowledgeBaseItem();
  const markPendingMutation = useMarkPendingKnowledgeBaseItem();

  // Filter out pending update clones from the tree — they show as a badge on the original instead
  const treeItems = items.filter(item => !item.original_item_id);

  // Find pending update proposals (keyed by original_item_id)
  const pendingUpdates = new Map<string, KnowledgeBaseItem>();
  for (const item of items) {
    if (item.original_item_id && item.status === 'pending') {
      pendingUpdates.set(item.original_item_id, item);
    }
  }

  const filteredItems = searchQuery.trim()
    ? treeItems.filter(item => {
        const q = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(q) ||
          item.tags?.some(t => t.toLowerCase().includes(q))
        );
      })
    : treeItems;

  const handleSelect = useCallback((item: KnowledgeBaseItem) => {
    setSelectedId(item.id, item.item_type);
  }, [setSelectedId]);

  const handleCreateFolder = useCallback((parentId: string | null) => {
    setCreateMode({ type: 'folder', parentId });
    setCreateName('');
  }, []);

  const handleCreateArticle = useCallback((parentId: string | null) => {
    setCreateMode({ type: 'article', parentId });
    setCreateName('');
  }, []);

  const handleRename = useCallback((item: KnowledgeBaseItem) => {
    setRenameItem(item);
    setRenameName(item.name);
  }, []);

  const handleMove = useCallback((item: KnowledgeBaseItem) => {
    setMoveItem(item);
  }, []);

  const handleDelete = useCallback((item: KnowledgeBaseItem) => {
    setDeleteItem(item);
  }, []);

  const submitCreate = async () => {
    if (!createMode || !createName.trim()) return;
    try {
      const newItem = await createMutation.mutateAsync({
        item_type: createMode.type,
        name: createName.trim(),
        parent_id: createMode.parentId,
        ...(createMode.type === 'article' ? { body: '', tags: [] } : {}),
      });
      setCreateMode(null);
      setSelectedId(newItem.id, createMode.type);
      showToast.success(`${createMode.type === 'folder' ? 'Folder' : 'Article'} created`);
    } catch {
      showToast.error('Failed to create item');
    }
  };

  const submitRename = async () => {
    if (!renameItem || !renameName.trim()) return;
    try {
      await saveMutation.mutateAsync({ id: renameItem.id, name: renameName.trim() });
      setRenameItem(null);
      showToast.success('Renamed');
    } catch {
      showToast.error('Failed to rename');
    }
  };

  const submitMove = async (itemId: string, newParentId: string | null) => {
    try {
      await moveMutation.mutateAsync({ id: itemId, newParentId });
      setMoveItem(null);
      showToast.success('Moved');
    } catch {
      showToast.error('Failed to move');
    }
  };

  const submitDelete = async () => {
    if (!deleteItem) return;
    try {
      await deleteMutation.mutateAsync(deleteItem.id);
      if (selectedId === deleteItem.id) setSelectedId(null);
      setDeleteItem(null);
      showToast.success('Deleted');
    } catch {
      showToast.error('Failed to delete');
    }
  };

  const handleArticleSave = async (updates: Partial<KnowledgeBaseItem>) => {
    try {
      await saveMutation.mutateAsync(updates);
      showToast.success('Article saved');
    } catch {
      showToast.error('Failed to save article');
    }
  };

  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync(id);
      showToast.success('Approved');
    } catch {
      showToast.error('Failed to approve');
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync(id);
      if (selectedId === id) setSelectedId(null);
      showToast.success('Rejected');
    } catch {
      showToast.error('Failed to reject');
    }
  };

  const handleMarkPending = async (id: string) => {
    try {
      await markPendingMutation.mutateAsync(id);
      showToast.success('Marked as pending');
    } catch {
      showToast.error('Failed to mark as pending');
    }
  };

  const handleDeleteArticle = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      setSelectedId(null);
      showToast.success('Article deleted');
    } catch {
      showToast.error('Failed to delete article');
    }
  };

  const selectedItem = treeItems.find(i => i.id === selectedId);
  const selectedPendingUpdate = selectedId ? pendingUpdates.get(selectedId) : undefined;

  return (
    <div className="flex flex-col h-svh overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-2">
          <Library className="h-5 w-5" />
          <h1 className="text-lg font-semibold">Knowledge Base</h1>
        </div>
        <div className="flex items-center gap-2">
          {canManageKB && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConsolidateOpen(true)}
              disabled={isLoading}
            >
              <Sparkles className="h-4 w-4 mr-1" />
              Consolidate
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleCreateFolder(null)}
          >
            <Folder className="h-4 w-4 mr-1" />
            New Folder
          </Button>
          <Button
            size="sm"
            onClick={() => handleCreateArticle(null)}
          >
            <FileText className="h-4 w-4 mr-1" />
            New Article
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 min-h-0">
        {/* Left panel: tree */}
        <div className="w-1/4 min-w-[200px] border-r flex flex-col h-full">
          <div className="px-3 py-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-8 h-8 text-sm"
              />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <FolderTree
                items={filteredItems}
                allItems={items}
                selectedId={selectedId}
                onSelect={handleSelect}
                onCreateFolder={handleCreateFolder}
                onCreateArticle={handleCreateArticle}
                onRename={handleRename}
                onMove={handleMove}
                onDelete={handleDelete}
              />
            )}
          </div>
        </div>

        {/* Right panel: editor */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          {selectedArticle && selectedItem?.item_type === 'article' ? (
            <div className="flex flex-col h-full min-h-0">
              {/* Pending approval banner for new articles */}
              {selectedItem.status === 'pending' && (
                <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800">
                  <Clock className="h-4 w-4 text-amber-600 shrink-0" />
                  <span className="text-sm text-amber-800 dark:text-amber-200 flex-1">
                    Proposed by Ellie — pending approval
                  </span>
                  {canManageKB && (
                    <>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(selectedItem.id)}
                        disabled={rejectMutation.isPending}
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-success text-success-foreground hover:bg-success/90"
                        onClick={() => handleApprove(selectedItem.id)}
                        disabled={approveMutation.isPending}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Approve
                      </Button>
                    </>
                  )}
                </div>
              )}

              {/* Pending update banner for active articles with a proposed edit */}
              {selectedPendingUpdate && (
                <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50 dark:bg-blue-950/30 border-b border-blue-200 dark:border-blue-800">
                  <Clock className="h-4 w-4 text-blue-600 shrink-0" />
                  <span className="text-sm text-blue-800 dark:text-blue-200 flex-1">
                    Ellie proposed an update to this article
                  </span>
                  {canManageKB && (
                    <>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(selectedPendingUpdate.id)}
                        disabled={rejectMutation.isPending}
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-success text-success-foreground hover:bg-success/90"
                        onClick={() => handleApprove(selectedPendingUpdate.id)}
                        disabled={approveMutation.isPending}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Approve Update
                      </Button>
                    </>
                  )}
                </div>
              )}

              <div className="flex-1 min-h-0">
                <ArticleEditor
                  article={selectedArticle}
                  pendingUpdateArticle={pendingUpdateArticle}
                  onSave={handleArticleSave}
                  saving={saveMutation.isPending}
                  readOnly={!canManageKB}
                  initialEditing={initialEditing}
                  onMarkPending={() => handleMarkPending(selectedArticle.id)}
                  onDelete={() => handleDeleteArticle(selectedArticle.id)}
                />
              </div>
            </div>
          ) : selectedItem?.item_type === 'folder' ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
              <Folder className="h-10 w-10" />
              <p className="text-sm font-medium">{selectedItem.name}</p>
              <p className="text-xs">
                {treeItems.filter(i => i.parent_id === selectedItem.id).length} items
              </p>
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => handleCreateArticle(selectedItem.id)}>
                  <FileText className="h-4 w-4 mr-1" />
                  New Article
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleCreateFolder(selectedItem.id)}>
                  <Folder className="h-4 w-4 mr-1" />
                  New Subfolder
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
              <Library className="h-10 w-10" />
              <p className="text-sm">Select an article or create a new one</p>
            </div>
          )}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createMode !== null} onOpenChange={open => !open && setCreateMode(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              New {createMode?.type === 'folder' ? 'Folder' : 'Article'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input
              value={createName}
              onChange={e => setCreateName(e.target.value)}
              placeholder={createMode?.type === 'folder' ? 'Folder name' : 'Article title'}
              onKeyDown={e => e.key === 'Enter' && submitCreate()}
              autoFocus
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateMode(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitCreate}
              disabled={!createName.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameItem !== null} onOpenChange={open => !open && setRenameItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Input
              value={renameName}
              onChange={e => setRenameName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitRename()}
              autoFocus
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameItem(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitRename}
              disabled={!renameName.trim() || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move dialog */}
      <MoveItemDialog
        open={moveItem !== null}
        onOpenChange={open => !open && setMoveItem(null)}
        item={moveItem}
        items={items}
        onMove={submitMove}
        moving={moveMutation.isPending}
      />

      {/* Delete confirmation */}
      <AlertDialog open={deleteItem !== null} onOpenChange={open => !open && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteItem?.item_type === 'folder' ? 'folder' : 'article'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItem?.item_type === 'folder'
                ? `This will permanently delete "${deleteItem?.name}" and all its contents.`
                : `This will permanently delete "${deleteItem?.name}".`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Consolidate dialog */}
      <ConsolidateKBDialog
        open={consolidateOpen}
        onOpenChange={setConsolidateOpen}
      />
    </div>
  );
}
